import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InstrumentTypeResponse } from "../../../codegen-api";
import { COLORS } from "../../../constants/design/colors";
import { FUNDING_RATE_PRECISION } from "../../../constants/precision/form";
import { getDefaultInstrumentType } from "../../../config/features";
import { MarketContext } from "../../../contexts/MarketContext";
import { IPerpsMarket } from "../../../contexts/MarketInstrumentContext/useGetMarkets";
import { useGetStatistics } from "../../../hooks/api/statistics/useGetStatistics";
import { AssetResponse, getAssetInfo } from "../../../utils/asset";
import { getAssetShortName } from "../../../utils/asset/assets";
import { getDisplayInstrumentName } from "../../../utils/displayName";
import { UNKNOWN_DISPLAY } from "../../../utils/unknownNumbers";
import { BaseModal } from "../../BaseModal";
import { InfoRow, Title, Value } from "../../TradeForm/style";
import TickerLogo from "../../shared/TickerLogo";
import {
  MarketDetailsDescription,
  MarketDetailsHeader,
  MarketDetailsInfo,
} from "./style";

/**
 * The instrument type a market surface reads: the explicit prop, else the
 * instrument's own, else the active market's — never a hard-coded Perpetual,
 * which sent a spot-only build's `/statistics` to the perp market (R-C).
 */
export const useResolvedInstrumentType = (
  instrumentType: InstrumentTypeResponse | undefined,
  instrument?: { instrument_type?: unknown }
): InstrumentTypeResponse => {
  const { market } = useContext(MarketContext);
  return (
    instrumentType ??
    (instrument?.instrument_type as InstrumentTypeResponse | undefined) ??
    market?.derivative ??
    getDefaultInstrumentType()
  );
};

interface IMarketDetailsModalProps {
  perpInstrument?: IPerpsMarket;
  instrumentType?: InstrumentTypeResponse;
  show: boolean;
  onHide: () => void;
}

/**
 * The market's trading rules. Shared by the desktop chart header and the
 * mobile market tabs (C-5), so both read the same rows.
 */
function MarketDetailsModal({
  perpInstrument,
  instrumentType,
  show,
  onHide,
}: IMarketDetailsModalProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "Perps.PerpsOrderbookAndChart",
  });
  const type = useResolvedInstrumentType(instrumentType, perpInstrument);
  const isSpot = type === InstrumentTypeResponse.Spot;

  // Funding exists for perpetuals only: a spot market fetches nothing here.
  const { data: statsData } = useGetStatistics(
    isSpot ? undefined : (perpInstrument?.underlying_asset as AssetResponse),
    type
  );
  // `funding_daily_avg` is the mean hourly rate over the last 24 settlements,
  // which is what the row's label says. It used to fall back to `funding_rate`
  // — the CURRENT estimate — under the same "average" label; absent, it is
  // unknown.
  const fundingDailyAvg = useMemo(() => {
    const raw = statsData?.funding_daily_avg;
    if (raw === null || raw === undefined || raw === "") return undefined;
    const value = Number(raw);
    return Number.isFinite(value) ? value : undefined;
  }, [statsData]);

  if (!perpInstrument) return null;

  const baseSymbol =
    getAssetShortName(perpInstrument.underlying_asset as AssetResponse) ||
    perpInstrument.underlying_asset;
  // Not in the generated OpenAPI type yet (see utils/instruments). The FLOOR an
  // order must reach — the row used to show `amount_step`, the rounding grid
  // (BTC 0.00000001 against a 0.00001 minimum), as "Min Contract Size" (B-3).
  const minOrderAmount = (perpInstrument as { min_order_amount?: string | number })
    .min_order_amount;
  const known = (v: unknown) => v !== null && v !== undefined && v !== "";

  return (
    <BaseModal
      style={{ width: 311 }}
      show={show}
      title={
        <MarketDetailsHeader>
          <TickerLogo asset={perpInstrument.underlying_asset} size={32} />
          {getDisplayInstrumentName(perpInstrument.instrument_name) ||
            perpInstrument.instrument_name}
        </MarketDetailsHeader>
      }
      onHide={onHide}
    >
      <MarketDetailsDescription>
        {getAssetInfo(perpInstrument.underlying_asset).description}
        <MarketDetailsInfo>
          <InfoRow>
            <Title>{t("min_order_size")}</Title>
            <Value>
              {known(minOrderAmount)
                ? `${minOrderAmount} ${baseSymbol}`
                : UNKNOWN_DISPLAY}
            </Value>
          </InfoRow>
          <InfoRow>
            <Title>{t("size_step")}</Title>
            <Value>
              {known(perpInstrument.amount_step)
                ? `${perpInstrument.amount_step} ${baseSymbol}`
                : UNKNOWN_DISPLAY}
            </Value>
          </InfoRow>
          <InfoRow>
            <Title>{t("min_notional_value")}</Title>
            <Value>
              {/*
                Read the market's own minimum. This was the literal string
                "10", so EVERY market claimed a 10 USDC minimum -- a trading
                rule the exchange does not have. The real value comes from
                the on-chain market rules (contract.client reads
                rules[3].minOrderValue) and varies per market: BTC-PERP's is
                ~0.00065 USDC, not 10.
              */}
              {perpInstrument.quote_asset && perpInstrument.min_order_value
                ? `${perpInstrument.min_order_value} ${perpInstrument.quote_asset}`
                : UNKNOWN_DISPLAY}
            </Value>
          </InfoRow>
          <InfoRow>
            <Title>{t("tick_size")}</Title>
            <Value>
              {known(perpInstrument.price_step)
                ? perpInstrument.price_step
                : UNKNOWN_DISPLAY}
            </Value>
          </InfoRow>
          <InfoRow>
            {/* Settlement is a derivatives term: spot trades against a quote. */}
            <Title>{isSpot ? t("quote_asset") : t("settlement_asset")}</Title>
            <Value>{perpInstrument.quote_asset ?? UNKNOWN_DISPLAY}</Value>
          </InfoRow>
          {!isSpot && (
            <InfoRow>
              <Title>{t("avg_1h_funding")}</Title>
              {fundingDailyAvg === undefined ? (
                <Value>{UNKNOWN_DISPLAY}</Value>
              ) : (
                <Value
                  style={{
                    color:
                      fundingDailyAvg >= 0 ? COLORS.up.one : COLORS.down.one,
                  }}
                >
                  {fundingDailyAvg >= 0 ? "+" : ""}
                  {(fundingDailyAvg * 100).toFixed(FUNDING_RATE_PRECISION)}%
                </Value>
              )}
            </InfoRow>
          )}
        </MarketDetailsInfo>
      </MarketDetailsDescription>
    </BaseModal>
  );
}

export default MarketDetailsModal;
